/**
 * UploadStep.jsx — Excel upload card with drag-and-drop.
 * Calls api.upload() on file selection or drop.
 */

import { useState, useRef, useCallback } from "react";
import { api } from "../api.js";

export default function UploadStep({ onUploaded, uploadInfo, onRunAll, onReset, runAllLoading, anyRunning, resetting }) {
  const [dragging,  setDragging]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState("");
  const [confirmReset, setConfirmReset] = useState(false);
  const inputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      setError("Please upload an .xlsx or .xls file.");
      return;
    }
    setError("");
    setUploading(true);
    try {
      const result = await api.upload(file);
      onUploaded(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }, [onUploaded]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  return (
    <div className="hm-upload-card">
      <div className="hm-upload-card__title">01 — Upload</div>

      {uploadInfo ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 18 }}>✅</span>
            <span style={{ fontWeight: 700, fontSize: 13 }}>{uploadInfo.filename}</span>
          </div>
          <div className="hm-upload-info">
            <strong>{uploadInfo.total_docs?.toLocaleString()}</strong> docs ·{" "}
            <strong>{uploadInfo.total_questions}</strong> questions
          </div>
          {uploadInfo.sample_urls?.length > 0 && (
            <div style={{ fontSize: 11, color: "var(--text-tertiary)", lineHeight: 1.6 }}>
              {uploadInfo.sample_urls.slice(0, 3).map((u, i) => (
                <div key={i} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {u}
                </div>
              ))}
              {uploadInfo.total_docs > 3 && (
                <div>+ {uploadInfo.total_docs - 3} more…</div>
              )}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
            <button
              className="hm-btn hm-btn--primary"
              onClick={onRunAll}
              disabled={runAllLoading || anyRunning || resetting}
            >
              {runAllLoading ? "Starting…" : anyRunning ? "Running…" : "▶ Run All Pipeline"}
            </button>
            <button
              className="hm-btn hm-btn--ghost hm-btn--sm"
              onClick={() => inputRef.current?.click()}
              disabled={anyRunning || resetting}
            >
              Replace File
            </button>
            {!confirmReset ? (
              <button
                className="hm-btn hm-btn--ghost hm-btn--sm"
                style={{ color: "#EF4444", borderColor: "#EF4444" }}
                onClick={() => setConfirmReset(true)}
                disabled={anyRunning || resetting}
              >
                ♻️ Reset All
              </button>
            ) : (
              <>
                <button
                  className="hm-btn hm-btn--sm"
                  style={{ background: "#EF4444", color: "#fff", border: "none" }}
                  onClick={() => { setConfirmReset(false); onReset && onReset(); }}
                  disabled={resetting}
                >
                  {resetting ? "Resetting…" : "Confirm Reset"}
                </button>
                <button
                  className="hm-btn hm-btn--ghost hm-btn--sm"
                  onClick={() => setConfirmReset(false)}
                >
                  Cancel
                </button>
              </>
            )}
          </div>
          {confirmReset && (
            <div style={{ fontSize: 11, color: "#EF4444", marginTop: 4 }}>
              ⚠️ This will clear all scraped, enriched, and scored data. Are you sure?
            </div>
          )}
        </div>
      ) : (
        <div
          className={`hm-dropzone${dragging ? " hm-dropzone--drag" : ""}`}
          onDrop={onDrop}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onClick={() => inputRef.current?.click()}
        >
          <div className="hm-dropzone__icon">{uploading ? "⏳" : "📄"}</div>
          <div className="hm-dropzone__text">
            {uploading ? "Uploading…" : "Drop Excel or click"}
          </div>
          <div className="hm-dropzone__hint">.xlsx / .xls</div>
        </div>
      )}

      {error && (
        <div style={{ fontSize: 11, color: "#EF4444", padding: "4px 0" }}>
          {error}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        onChange={onInputChange}
      />
    </div>
  );
}
